import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, ImageOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCMS } from "@/hooks/useCMS";
import { useAuth } from "@/context/AuthContext";
import { MediaPicker } from "@/components/MediaPicker";
import { SCHEMAS } from "@/pages/admin/schemas";

// Which field holds the image, the label field, and the alt-text field, per collection.
const IMAGE_MAP = {
  events: { field: "image", label: "title", alt: "image_alt" },
  books: { field: "cover", label: "title", alt: "cover_alt" },
  campaigns: { field: "image", label: "name", alt: "image_alt" },
  participate: { field: "image", label: "title", alt: "image_alt" },
  board: { field: "image", label: "name", alt: "image_alt" },
  panels: { field: "image", label: "title", alt: "image_alt" },
  partners: { field: "logo", label: "name", alt: "logo_alt" },
  articles: { field: "cover", label: "title", alt: "cover_alt" },
  gallery: { field: "url", label: "alt", alt: "alt" },
};

function ImageTile({ src, title, altValue, onReplace, onAltSave, readOnly, testid }) {
  const [alt, setAlt] = useState(altValue || "");
  useEffect(() => { setAlt(altValue || ""); }, [altValue]);
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card" data-testid={testid}>
      <div className="flex h-36 items-center justify-center overflow-hidden bg-muted">
        {src ? <img src={src} alt={alt || title} className="h-full w-full object-cover" /> : <ImageOff className="text-muted-foreground" />}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <p className="truncate text-sm font-semibold">{title || "Untitled"}</p>
        {!readOnly && (
          <input
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            onBlur={() => { if ((altValue || "") !== alt) onAltSave(alt); }}
            placeholder="Alt text (accessibility)"
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#2CACD4]"
            data-testid={`${testid}-alt`}
          />
        )}
        {!readOnly && <MediaPicker onPick={onReplace} />}
      </div>
    </div>
  );
}

export default function SiteImages() {
  const { reload } = useCMS();
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [data, setData] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const cols = Object.keys(IMAGE_MAP);
      const [s, ...results] = await Promise.all([
        api.get("/settings"),
        ...cols.map((c) => api.get(`/content/${c}`)),
      ]);
      setSettings(s.data || {});
      const next = {};
      cols.forEach((c, i) => { next[c] = results[i].data || []; });
      setData(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveLogoField = async (key, url) => {
    try {
      await api.put("/settings", { ...settings, [key]: url });
      setSettings((s) => ({ ...s, [key]: url }));
      toast.success("Logo updated"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateItem = async (col, item, patch) => {
    try {
      await api.put(`/content/${col}/${item.id}`, patch);
      setData((d) => ({ ...d, [col]: d[col].map((it) => (it.id === item.id ? { ...it, ...patch } : it)) }));
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>;

  return (
    <div data-testid="admin-site-images">
      <h1 className="font-display font-black uppercase text-3xl flex items-center gap-3"><ImageIcon className="text-[#2CACD4]" /> Site Images</h1>
      <p className="mt-1 text-muted-foreground text-sm">Replace any image used across the site — the logo and every content image — and set its alt text, all in one place. To adjust focal point/zoom, open the item in its own content editor.</p>

      {/* Brand / logos */}
      <div className="mt-6">
        <h2 className="font-display font-black uppercase text-lg">Brand</h2>
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card" data-testid="site-image-logo">
            <div className="flex h-36 items-center justify-center overflow-hidden bg-[#0F172A] px-4">
              {settings.logo ? <img src={settings.logo} alt="Logo" className="max-h-16 w-auto object-contain" /> : <span className="text-xs text-white/50">No logo</span>}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-3">
              <p className="truncate text-sm font-semibold">Site logo</p>
              {!readOnly && <MediaPicker onPick={(url) => saveLogoField("logo", url)} />}
            </div>
          </div>
          <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card" data-testid="site-image-logo-light">
            <div className="flex h-36 items-center justify-center overflow-hidden bg-[#0F172A] px-4">
              {(settings.logo_light || settings.logo) ? <img src={settings.logo_light || settings.logo} alt="Light logo" className="max-h-16 w-auto object-contain" /> : <span className="text-xs text-white/50">No logo</span>}
            </div>
            <div className="flex flex-1 flex-col gap-2 p-3">
              <p className="truncate text-sm font-semibold">Logo — light (dark bg)</p>
              {!readOnly && <MediaPicker onPick={(url) => saveLogoField("logo_light", url)} />}
            </div>
          </div>
        </div>
      </div>

      {Object.keys(IMAGE_MAP).map((col) => {
        const { field, label, alt } = IMAGE_MAP[col];
        const items = data[col] || [];
        if (!items.length) return null;
        return (
          <div className="mt-8" key={col}>
            <h2 className="font-display font-black uppercase text-lg">{SCHEMAS[col]?.label || col}</h2>
            <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((it) => (
                <ImageTile
                  key={it.id}
                  src={it[field]}
                  title={it[label]}
                  altValue={it[alt]}
                  readOnly={readOnly}
                  onReplace={(url) => updateItem(col, it, { [field]: url }).then(() => toast.success("Image replaced"))}
                  onAltSave={(val) => updateItem(col, it, { [alt]: val }).then(() => toast.success("Alt text saved"))}
                  testid={`site-image-${col}-${it.id}`}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
